/**
 * Auto-scrolls a container when the cursor is dragged near its edges.
 *
 * Used during selection drag in the mouse state machine (see
 * `mouse-state-machine.ts`): when the user extends a selection by dragging and
 * the cursor reaches the edge of the scroll container, we scroll so the user can
 * keep selecting beyond the visible area. Each scroll tick we extend the
 * selection to the cell under the (clamped) cursor via the onTick callback.
 *
 * The scroll loop starts when the cursor enters the edge zone; it stops when the
 * scroll vector is zero (no scroll needed or scroll limit reached).
 */

const DEFAULT_EDGE_THRESHOLD = 150;
const DEFAULT_MAX_SPEED = 20;
const CLAMP_INSET = 5;

export interface AutoScrollerConfig {
  getScrollContainer: () => HTMLElement | null;
  edgeThreshold?: number;
  maxSpeed?: number;
}

export interface AutoScroller {
  update(x: number, y: number, options?: AutoScrollUpdateOptions): void;
  stop(): void;
}

export interface AutoScrollUpdateOptions {
  /**
   * Disables horizontal movement without affecting vertical auto-scroll.
   */
  horizontalAutoScroll?: 'enabled' | 'disabled';
  /**
   * Overrides the visible scrolling region's left edge when fixed content
   * occludes the scroll container.
   */
  horizontalAutoScrollViewportLeft?: number;
}

interface ScrollVector {
  x: number;
  y: number;
}

interface ComputeScrollVectorParams {
  cursorX: number;
  cursorY: number;
  rect: DOMRect;
  container: HTMLElement;
  edgeThreshold: number;
  maxSpeed: number;
  horizontalAutoScrollEnabled: boolean;
  horizontalAutoScrollViewportLeft?: number;
}

function computeScrollVector(params: ComputeScrollVectorParams): ScrollVector {
  const {
    cursorX,
    cursorY,
    rect,
    container,
    edgeThreshold,
    maxSpeed,
    horizontalAutoScrollEnabled,
    horizontalAutoScrollViewportLeft
  } = params;

  let x = 0;
  let y = 0;

  const left = horizontalAutoScrollViewportLeft ?? rect.left;
  const distFromLeft = cursorX - left;
  const distFromRight = rect.right - cursorX;
  const distFromTop = cursorY - rect.top;
  const distFromBottom = rect.bottom - cursorY;

  if (horizontalAutoScrollEnabled) {
    if (distFromLeft < edgeThreshold && container.scrollLeft > 0) {
      x = -maxSpeed * (1 - distFromLeft / edgeThreshold);
    } else if (
      distFromRight < edgeThreshold &&
      container.scrollLeft < container.scrollWidth - container.clientWidth
    ) {
      x = maxSpeed * (1 - distFromRight / edgeThreshold);
    }
  }

  if (distFromTop < edgeThreshold && container.scrollTop > 0) {
    y = -maxSpeed * (1 - distFromTop / edgeThreshold);
  } else if (
    distFromBottom < edgeThreshold &&
    container.scrollTop < container.scrollHeight - container.clientHeight
  ) {
    y = maxSpeed * (1 - distFromBottom / edgeThreshold);
  }

  return { x, y };
}

/**
 * Coordinates are clamped so that when the cursor is past the edge we still pass
 * a point inside the container; the consumer (e.g. getClickTarget) then gets a
 * valid cell at that edge to extend the selection to, instead of no cell.
 */

function clampToRect({
  x,
  y,
  rect,
  horizontalAutoScrollViewportLeft
}: {
  x: number;
  y: number;
  rect: DOMRect;
  horizontalAutoScrollViewportLeft?: number;
}): { x: number; y: number } {
  const left = horizontalAutoScrollViewportLeft ?? rect.left;

  return {
    x: Math.max(left + CLAMP_INSET, Math.min(rect.right - CLAMP_INSET, x)),
    y: Math.max(rect.top + CLAMP_INSET, Math.min(rect.bottom - CLAMP_INSET, y))
  };
}

/**
 * Creates an auto-scroller that scrolls the container when the cursor is in the
 * edge zone. Calls onTick each frame with the cursor position clamped inside the
 * container (with a small inset).
 *
 * @param config - Container resolver and optional edgeThreshold / maxSpeed
 * @param onTick - Called each scroll frame with (clampedX, clampedY) in viewport coords
 */
export function createAutoScroller(
  config: AutoScrollerConfig,
  onTick: (clampedX: number, clampedY: number) => void
): AutoScroller {
  const edgeThreshold = config.edgeThreshold ?? DEFAULT_EDGE_THRESHOLD;
  const maxSpeed = config.maxSpeed ?? DEFAULT_MAX_SPEED;

  let lastX = 0;
  let lastY = 0;
  let lastOptions: AutoScrollUpdateOptions = {};
  let frameId: number | null = null;

  function loop() {
    const container = config.getScrollContainer();

    if (!container) {
      frameId = null;
      return;
    }

    const rect = container.getBoundingClientRect();
    const vector = computeScrollVector({
      cursorX: lastX,
      cursorY: lastY,
      rect,
      container,
      edgeThreshold,
      maxSpeed,
      horizontalAutoScrollEnabled: lastOptions.horizontalAutoScroll !== 'disabled',
      horizontalAutoScrollViewportLeft: lastOptions.horizontalAutoScrollViewportLeft
    });

    if (vector.x === 0 && vector.y === 0) {
      frameId = null;
      return;
    }

    container.scrollBy(vector.x, vector.y);

    const clamped = clampToRect({
      x: lastX,
      y: lastY,
      rect,
      horizontalAutoScrollViewportLeft: lastOptions.horizontalAutoScrollViewportLeft
    });
    onTick(clamped.x, clamped.y);

    frameId = requestAnimationFrame(loop);
  }

  function startIfNeeded() {
    if (frameId !== null) {
      return;
    }

    frameId = requestAnimationFrame(loop);
  }

  function isNearEdge({
    x,
    y,
    rect,
    options
  }: {
    x: number;
    y: number;
    rect: DOMRect;
    options: AutoScrollUpdateOptions;
  }): boolean {
    const left = options.horizontalAutoScrollViewportLeft ?? rect.left;

    return (
      (options.horizontalAutoScroll !== 'disabled' &&
        (x - left < edgeThreshold || rect.right - x < edgeThreshold)) ||
      y - rect.top < edgeThreshold ||
      rect.bottom - y < edgeThreshold
    );
  }

  return {
    update(x: number, y: number, options = {}) {
      lastX = x;
      lastY = y;
      lastOptions = options;

      const container = config.getScrollContainer();

      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();

      if (isNearEdge({ x, y, rect, options })) {
        startIfNeeded();
      }
    },

    stop() {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    }
  };
}
